/**
 * 結果画面。enrollment の成否を表示する。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の結果画面コンポーネント (success / failure)
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import type { MessagesCatalog } from '../i18n/index.js';

interface ResultPageProps {
  messages: MessagesCatalog;
  variant: 'success' | 'failure';
  onRestart: () => void;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business enrollment 完了 / 失敗の結果画面。restart で初期画面に戻る
 * @graph-connects none
 */
export function ResultPage({ messages, variant, onRestart }: ResultPageProps): ReactNode {
  const { result } = messages;
  const title = variant === 'success' ? result.successTitle : result.failureTitle;
  const description = variant === 'success' ? result.successDescription : result.failureDescription;
  const isSuccess = variant === 'success';

  return (
    <Card>
      <div className="flex flex-col items-center gap-6 text-center">
        <div
          className={[
            'flex h-16 w-16 items-center justify-center rounded-full ring-1',
            isSuccess
              ? 'bg-[var(--color-success-soft)] text-[var(--color-success)] ring-[var(--color-success)]/40'
              : 'bg-[var(--color-danger-soft)] text-[var(--color-danger)] ring-[var(--color-danger)]/40',
          ].join(' ')}
        >
          {isSuccess ? <CheckIcon /> : <CrossIcon />}
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
            {title}
          </h2>
          <p className="text-[var(--color-text-secondary)] leading-relaxed">{description}</p>
        </div>
        <Button variant="secondary" onClick={onRestart}>
          {result.backLabel}
        </Button>
      </div>
    </Card>
  );
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 成功結果画面に表示するチェックマークアイコン SVG
 * @graph-connects none
 */
function CheckIcon(): ReactNode {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 失敗結果画面に表示するバツ印アイコン SVG
 * @graph-connects none
 */
function CrossIcon(): ReactNode {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
